// Field input controls (form-view slice). One control per field
// type, dispatched by `FieldInput`. Masks come from valueFormat so what the
// student types lands in the same storage format extraction produces.
//
// `select` renders as a button that opens the picker sheet (searchable past 8
// options — see PickerSheet); `address` carries the existing Google Places
// integration (Shared/Form/GoogleAddressField's loader + parser) and degrades
// to a plain input when no API key is configured.

import React from 'react';
import { loadMaps, parse as parseGooglePlace } from '../Shared/Form/GoogleAddressField';
import { AddressBag } from './addressFill';
import styles from './ConversationalForms.module.css';
import { CfField, CfGroupInstance, FieldValue } from './types';
import { registerPacContainer, unregisterPacContainer } from './useFocusTrap';
import { countWords } from './validation';
import { asInstances, fmtPhone, fmtSSN, fmtZip, maskSSN } from './valueFormat';

/** HTML autocomplete mapping — mirrors production getAutoComplete. */
export const autoCompleteFor = (field: CfField): string => {
    const label = field.label.toLowerCase().replace(/[^a-z]/g, '');
    if (label.includes('firstname')) return 'given-name';
    if (label.includes('lastname')) return 'family-name';
    if (label === 'name' || label === 'fullname') return 'name';
    if (field.type === 'email') return 'email';
    if (field.type === 'phone') return 'tel';
    if (field.type === 'zip') return 'postal-code';
    return 'off';
};

/** Progressive MM/DD/YYYY mask — slashes appear as the digits arrive. */
export const maskDate = (raw: string): string => {
    const d = raw.replace(/\D/g, '').slice(0, 8);
    if (d.length < 3) return d;
    if (d.length < 5) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

/** Progressive MM/YYYY mask. */
export const maskMonthYear = (raw: string): string => {
    const d = raw.replace(/\D/g, '').slice(0, 6);
    return d.length < 3 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
};

const MASKS: Partial<Record<CfField['type'], (raw: string) => string>> = {
    date: maskDate,
    monthyear: maskMonthYear,
    phone: (raw) => fmtPhone(raw),
    zip: (raw) => fmtZip(raw),
    ssn: (raw) => fmtSSN(raw),
};

export type FieldInputProps = {
    field: CfField;
    value: FieldValue | undefined;
    onChange: (value: FieldValue) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onOpenPicker?: (fieldId: string) => void;
    /** A Google Places pick — the parsed `Address:*` bag the overlay turns
     *  into a multi-fill across the address field and its siblings. */
    onAddressPick?: (fieldId: string, bag: AddressBag) => void;
    autoFocus?: boolean;
};

const TextControl = ({ field, value, onChange, onFocus, onBlur, autoFocus }: FieldInputProps) => {
    const mask = MASKS[field.type];
    const numeric = field.type === 'number' || Boolean(mask);
    return (
        <input
            className={styles.input}
            type={field.type === 'email' ? 'email' : 'text'}
            inputMode={field.type === 'email' ? 'email' : numeric ? 'numeric' : undefined}
            value={String(value ?? '')}
            onChange={(e) => {
                const raw = e.target.value;
                if (field.type === 'number') {
                    onChange(raw.replace(/[^\d.]/g, '').slice(0, 8));
                } else {
                    onChange(mask ? mask(raw) : raw);
                }
            }}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autoFocus}
            autoComplete={autoCompleteFor(field)}
            aria-label={field.label}
            aria-describedby={field.help ? `${field.id}-help` : undefined}
        />
    );
};

/** Sensitive masked input — digits stored, last-4 shown, Show/Hide toggle. */
const SsnControl = (props: FieldInputProps) => {
    const [show, setShow] = React.useState(false);
    const { field, value, onChange, onFocus, onBlur, autoFocus } = props;
    const stored = String(value ?? '');
    return (
        <div className={styles.ssnWrap}>
            <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={stored ? (show ? stored : maskSSN(stored)) : ''}
                onChange={(e) => onChange(fmtSSN(e.target.value))}
                onFocus={onFocus}
                onBlur={onBlur}
                autoFocus={autoFocus}
                autoComplete="off"
                aria-label={field.label}
            />
            {stored && (
                <button
                    type="button"
                    className={styles.ssnReveal}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShow((s) => !s)}
                >
                    {show ? 'Hide' : 'Show'}
                </button>
            )}
        </div>
    );
};

const RadioControl = ({ field, value, onChange }: FieldInputProps) => (
    <div className={styles.radios} role="radiogroup" aria-label={field.label}>
        {(field.options ?? []).map((option) => {
            const selected = value === option.value;
            return (
                <button
                    type="button"
                    key={option.value}
                    role="radio"
                    aria-checked={selected}
                    className={`${styles.radio} ${selected ? styles.radioSelected : ''}`}
                    onClick={() => onChange(option.value)}
                >
                    <span className={styles.radioDot} aria-hidden="true" />
                    <span>{option.label}</span>
                </button>
            );
        })}
    </div>
);

/** The string members of a checklist/consent value — group instances are not
 *  selections, and TS cannot filter the raw array union directly. */
const selectedStrings = (value: FieldValue | undefined): string[] => {
    const held: Array<string | unknown> = Array.isArray(value) ? value : [];
    return held.filter((v): v is string => typeof v === 'string');
};

/** "None of these apply" (value `none`) is mutually exclusive with the rest. */
const ChecklistControl = ({ field, value, onChange }: FieldInputProps) => {
    const selected = selectedStrings(value);
    const toggle = (v: string) => {
        if (v === 'none') {
            onChange(selected.includes('none') ? [] : ['none']);
            return;
        }
        const rest = selected.filter((x) => x !== 'none');
        onChange(rest.includes(v) ? rest.filter((x) => x !== v) : [...rest, v]);
    };
    return (
        <div className={styles.checks} role="group" aria-label={field.label}>
            {(field.options ?? []).map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                    <button
                        type="button"
                        key={option.value}
                        role="checkbox"
                        aria-checked={isSelected}
                        className={`${styles.check} ${isSelected ? styles.checkSelected : ''}`}
                        onClick={() => toggle(option.value)}
                    >
                        <span className={styles.checkBox} aria-hidden="true">
                            {isSelected ? '✓' : ''}
                        </span>
                        <span>{option.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * Consent attestations: the authored legal copy renders verbatim, every line
 * is required (statusOf withholds `confirmed` until all are checked), nothing
 * is ever pre-checked, and — unlike the checklist — there is no mutually
 * exclusive "none" option: each line toggles independently.
 */
const ConsentControl = ({ field, value, onChange }: FieldInputProps) => {
    const checked = selectedStrings(value);
    const toggle = (v: string) =>
        onChange(checked.includes(v) ? checked.filter((x) => x !== v) : [...checked, v]);
    return (
        <div className={`${styles.checks} ${styles.consent}`} role="group" aria-label={field.label}>
            {(field.options ?? []).map((option) => {
                const isChecked = checked.includes(option.value);
                return (
                    <button
                        type="button"
                        key={option.value}
                        role="checkbox"
                        aria-checked={isChecked}
                        className={`${styles.check} ${styles.consentCheck} ${isChecked ? styles.checkSelected : ''}`}
                        onClick={() => toggle(option.value)}
                    >
                        <span className={styles.checkBox} aria-hidden="true">
                            {isChecked ? '✓' : ''}
                        </span>
                        <span>{option.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

const SelectControl = ({ field, value, onOpenPicker }: FieldInputProps) => {
    const display = field.options?.find((o) => o.value === value)?.label ?? String(value ?? '');
    return (
        <button
            type="button"
            className={`${styles.input} ${styles.selectButton}`}
            onClick={() => onOpenPicker?.(field.id)}
            aria-haspopup="dialog"
            aria-label={field.label}
        >
            <span className={display ? undefined : styles.selectPlaceholder}>
                {display || 'Select…'}
            </span>
        </button>
    );
};

/** Long-form writing with a live word counter against `maxWords` — the
 *  counter reads "{n} / {max} words" and flips to the over state past the
 *  limit, announced via aria-describedby (delta spec "Word count tracks the
 *  essay"). No `maxWords`, no counter. */
const LongTextControl = ({ field, value, onChange, onFocus, onBlur }: FieldInputProps) => {
    const text = String(value ?? '');
    const words = countWords(text);
    const over = Boolean(field.maxWords && words > field.maxWords);
    return (
        <div className={styles.longTextWrap}>
            <textarea
                className={`${styles.input} ${styles.longText}`}
                rows={5}
                value={text}
                onChange={(e) => onChange(e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
                aria-label={field.label}
                aria-describedby={field.maxWords ? `${field.id}-wc` : undefined}
            />
            {field.maxWords ? (
                <div
                    id={`${field.id}-wc`}
                    className={`${styles.wordCount} ${over ? styles.wordCountOver : ''}`}
                >
                    {words} / {field.maxWords} words
                </div>
            ) : null}
        </div>
    );
};

/**
 * Repeating group: instance cards over the field's `template`, "Add" within
 * `repeat.max`, per-instance remove. `repeat.min` is enforced by the status
 * machine (a short group never reads confirmed), not by disabling remove.
 * Form-view-local until the multi-instance backend lands — forms carrying
 * `group` aren't offered conversationally (design risk note), and the type
 * stays in NEVER_AI.
 */
const GroupControl = ({ field, value, onChange }: FieldInputProps) => {
    const instances = asInstances(value);
    const max = field.repeat?.max ?? 10;
    const template = field.template ?? [];
    const itemLabel = field.itemLabel || 'Item';
    const update = (index: number, subId: string, subValue: FieldValue) =>
        onChange(
            instances.map((instance, i) =>
                i === index
                    ? ({ ...instance, [subId]: subValue } as CfGroupInstance)
                    : instance
            )
        );
    return (
        <div className={styles.group} role="group" aria-label={field.label}>
            {instances.map((instance, index) => (
                <div className={styles.groupInstance} key={index} data-testid={`cf-group-${field.id}-${index}`}>
                    <div className={styles.groupInstanceHead}>
                        <span>{`${itemLabel} ${index + 1}`}</span>
                        <button
                            type="button"
                            className={styles.groupRemove}
                            onClick={() => onChange(instances.filter((_, i) => i !== index))}
                            aria-label={`Remove ${itemLabel.toLowerCase()} ${index + 1}`}
                        >
                            ✕
                        </button>
                    </div>
                    {template.map((sub) => (
                        <div className={styles.groupSubRow} key={sub.id}>
                            <label className={styles.groupSubLabel}>
                                {sub.label}
                                {!sub.optional && (
                                    <span className={styles.requiredStar} aria-hidden="true"> *</span>
                                )}
                            </label>
                            <FieldInput
                                field={sub}
                                value={instance[sub.id]}
                                onChange={(v) => update(index, sub.id, v)}
                            />
                        </div>
                    ))}
                </div>
            ))}
            {instances.length < max && (
                <button
                    type="button"
                    className={styles.groupAdd}
                    onClick={() => onChange([...instances, {}])}
                >
                    + Add {itemLabel.toLowerCase()}
                </button>
            )}
        </div>
    );
};

/**
 * Address with the production Google Places integration: the same idempotent
 * loader and env key as the legacy form (Shared/Form/GoogleAddressField), a
 * `types: ['geocode']` Autocomplete on this input, and a pick surfaced as the
 * parsed bag so the overlay can multi-fill street + siblings in one action.
 * Without an API key it behaves exactly like the plain text input it was.
 */
const AddressControl = (props: FieldInputProps) => {
    const { field, value, onChange, onFocus, onBlur, autoFocus, onAddressPick } = props;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const attachedRef = React.useRef(false);
    // The Places listener outlives renders; read the latest pick handler
    // through a ref so it never goes stale.
    const pickRef = React.useRef(onAddressPick);
    pickRef.current = onAddressPick;
    // Kept for unmount cleanup — address rows remount on every collapse/
    // expand (form-view slice), so without this every toggle left behind a
    // live Autocomplete instance, its `place_changed` listener, and its
    // `.pac-container` (Places appends this straight to `document.body`,
    // outside this component's own DOM, and gives no handle to it).
    // `clearInstanceListeners` only reclaims the `place_changed` listener
    // and (below) the container — it does NOT undo whatever Places attached
    // to the input itself or to `document`, and Google's own internal
    // registry keeps the instance regardless. This narrows the leak; it does
    // not eliminate it.
    const autocompleteRef = React.useRef<any>(null);
    const pacContainerRef = React.useRef<HTMLElement | null>(null);

    React.useEffect(() => {
        const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!key) return;
        // Captured at setup: by the time cleanup runs the ref may point at a
        // different node (or null), and we must unregister the exact node we
        // registered.
        const inputEl = inputRef.current;
        let disposed = false;
        loadMaps(key, () => {
            if (disposed || attachedRef.current || !inputRef.current) return;
            const google = (window as any).google;
            if (!google?.maps?.places?.Autocomplete) return;
            // Read the count BEFORE construction: construction appends this
            // instance's `.pac-container` to document.body synchronously, so
            // comparing before/after tells us whether one actually landed —
            // if Places ever creates it lazily instead (e.g. on first
            // keystroke, not in the constructor), the count won't have grown
            // yet. Capturing "whatever's newest" unconditionally in that case
            // would grab a DIFFERENT, already-mounted sibling field's live
            // container, and this field's own cleanup would later remove it
            // out from under that sibling — killing its suggestions for the
            // rest of the session, strictly worse than the leak this fix
            // targets. A benign leak beats stealing a sibling's container, so
            // this only ever captures its own.
            const before = document.querySelectorAll('.pac-container').length;
            const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
                types: ['geocode'],
            });
            autocomplete.addListener('place_changed', () => {
                const bag = parseGooglePlace(autocomplete.getPlace());
                pickRef.current?.(field.id, bag);
            });
            attachedRef.current = true;
            autocompleteRef.current = autocomplete;
            const containers = document.querySelectorAll<HTMLElement>('.pac-container');
            pacContainerRef.current = containers.length > before ? containers[containers.length - 1] : null;
            // The Esc-vs-dropdown fix (`useFocusTrap.ts`) needs to know
            // which container belongs to THIS input to scope its check —
            // Places gives no such link itself.
            if (pacContainerRef.current && inputRef.current) {
                registerPacContainer(inputRef.current, pacContainerRef.current);
            }
        });
        return () => {
            disposed = true;
            const google = (window as any).google;
            if (autocompleteRef.current && google?.maps?.event) {
                google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
            autocompleteRef.current = null;
            if (inputEl) unregisterPacContainer(inputEl);
            pacContainerRef.current?.remove();
            pacContainerRef.current = null;
            // Reset last: a double-invoked effect (StrictMode, with
            // `loadMaps`'s callback firing synchronously) runs cleanup then
            // setup again in the same pass. Without resetting this, pass 2's
            // setup would see it still `true` and bail — permanently, since
            // nothing else ever flips it back — leaving the field with no
            // Autocomplete at all. Not reachable today (this file renders
            // outside StrictMode), but cheap to not leave as a trap.
            attachedRef.current = false;
        };
    }, [field.id]);

    return (
        <input
            ref={inputRef}
            className={styles.input}
            type="text"
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autoFocus}
            autoComplete="off"
            aria-label={field.label}
            aria-describedby={field.help ? `${field.id}-help` : undefined}
        />
    );
};

const DateRangeControl = ({ field, value, onChange, onFocus, onBlur }: FieldInputProps) => {
    const [from, to] = String(value ?? '|').split('|');
    return (
        <div className={styles.dateRange}>
            <input
                className={styles.input}
                inputMode="numeric"
                placeholder="From MM/YYYY"
                value={from ?? ''}
                onChange={(e) => onChange(`${maskMonthYear(e.target.value)}|${to ?? ''}`)}
                onFocus={onFocus}
                onBlur={onBlur}
                autoComplete="off"
                aria-label={`${field.label} from`}
            />
            <span className={styles.dateRangeSep} aria-hidden="true">
                –
            </span>
            <input
                className={styles.input}
                inputMode="numeric"
                placeholder="To MM/YYYY"
                value={to ?? ''}
                onChange={(e) => onChange(`${from ?? ''}|${maskMonthYear(e.target.value)}`)}
                onFocus={onFocus}
                onBlur={onBlur}
                autoComplete="off"
                aria-label={`${field.label} to`}
            />
        </div>
    );
};

export const FieldInput = (props: FieldInputProps) => {
    switch (props.field.type) {
        case 'select':
            return <SelectControl {...props} />;
        case 'radio':
            return <RadioControl {...props} />;
        case 'checklist':
            return <ChecklistControl {...props} />;
        case 'consent':
            return <ConsentControl {...props} />;
        case 'longtext':
            return <LongTextControl {...props} />;
        case 'daterange':
            return <DateRangeControl {...props} />;
        case 'group':
            return <GroupControl {...props} />;
        case 'address':
            return <AddressControl {...props} />;
        case 'ssn':
            return props.field.sensitive ? <SsnControl {...props} /> : <TextControl {...props} />;
        default:
            // text/email/date/monthyear/number/phone/zip.
            return <TextControl {...props} />;
    }
};
